import {Image, Pressable, View} from 'react-native'
import {type MessageDescriptor} from '@lingui/core'
import {msg, plural} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'

import {DraggableScrollView} from '#/view/com/pager/DraggableScrollView'
import {atoms as a, useTheme} from '#/alf'
import {Text} from '#/components/Typography'
import {useSemanticTokens} from '#/wsocial/service/design-system'
import {useTrendLabels} from '../data/labels'
import {type Trend, type TrendSection} from '../data/trends'
import {getVoicesDisplay, useTrendSearchPress} from '../util'
import {ImageWithPlaceholder} from './ImageWithPlaceholder'
import {TrendImageFallback} from './TrendImageFallback'

/**
 * Carousel trends section.
 *
 * Used when `section.layout === 'carousel'`. Mirrors Figma nodes `384:53703`
 * ("Trending topics") and `384:53750` ("Trending within sport") in file
 * `C8J3bA4ThNVCG0vzlHeI16`. Both frames are visually identical — only the
 * section title differs, which comes from `section.title`.
 *
 * The carousel pattern is **section-level**, not prominence-driven. A section
 * with `layout === 'carousel'` containing hero-prominence trends still renders
 * with these small cards. Per `trends-how-it-works.md`, backend controls
 * layout per section and may emit a dedicated carousel section to compile
 * featured-prominence trends together.
 */
export function CarouselTrendsSection({section}: {section: TrendSection}) {
  const t = useTheme()

  return (
    <View
      style={[
        a.w_full,
        a.pl_lg,
        a.gap_lg,
        // Top divider is owned by TrendSectionView so it's consistent across
        // every section type — don't draw one here.
        {paddingTop: 24, paddingBottom: 32},
      ]}>
      {section.title && (
        <Text style={[a.text_md, a.font_bold, t.atoms.text]}>
          {section.title}
        </Text>
      )}
      <DraggableScrollView
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={[a.flex_row, a.gap_sm, {paddingRight: 16}]}>
        {section.trends.map(trend => (
          <CarouselTrendCard key={trend.id} trend={trend} />
        ))}
      </DraggableScrollView>
    </View>
  )
}

function CarouselTrendCard({trend}: {trend: Trend}) {
  const t = useTheme()
  const ss = useSemanticTokens()
  const {_} = useLingui()
  const goToTrendSearch = useTrendSearchPress()
  const {getCategoryLabel} = useTrendLabels()

  const visibleAvatars = trend.participantAvatars.slice(0, 3)
  const voices = getVoicesDisplay(trend.metrics.activeVoicesCount)
  // 2–5 voices: just the word, no number; >5: compact count + word.
  const voicesLabel = voices.showCount
    ? formatVoicesLabel(trend.metrics.activeVoicesCount ?? 0, _)
    : _(msg`voices`)

  const imageFallback = (
    <TrendImageFallback
      style={{width: '100%', height: '100%'}}
      tileSize={40}
      variant={trend.featuredCards.length > 0 ? 'globe' : 'brand'}
    />
  )

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={_(msg`Explore trend: ${trend.title}`)}
      accessibilityHint={_(msg`Searches for this trend`)}
      onPress={() => goToTrendSearch(trend)}
      style={[{width: 176}]}>
      {/* Image header */}
      <View
        style={[
          {
            height: 92,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            overflow: 'hidden',
            backgroundColor: ss.bg.contrast_25,
          },
        ]}>
        {/* The fallback always renders; a real image is drawn over it and
            only appears once loaded, so slow or blocked publishers show the
            fallback instead of a blank. Smaller tile — this image area is
            only 92px tall. */}
        {trend.heroImage ? (
          <ImageWithPlaceholder
            uri={trend.heroImage.url}
            style={{width: '100%', height: '100%'}}
            placeholder={imageFallback}
          />
        ) : (
          imageFallback
        )}
      </View>

      {/* Content panel */}
      <View
        style={[
          a.gap_sm,
          a.justify_center,
          {
            height: 112,
            paddingTop: 12,
            paddingBottom: 14,
            paddingHorizontal: 14,
            borderBottomLeftRadius: 16,
            borderBottomRightRadius: 16,
            backgroundColor: ss.bg.contrast_25,
          },
        ]}>
        <Text
          style={[
            a.text_xs,
            a.font_medium,
            a.leading_snug,
            t.atoms.text_contrast_medium,
          ]}
          numberOfLines={1}>
          {getCategoryLabel(trend.category)}
        </Text>

        <View style={[a.flex_1, a.justify_between]}>
          <Text
            style={[a.text_md, a.font_semi_bold, a.leading_snug, t.atoms.text]}
            numberOfLines={2}>
            {trend.title}
          </Text>

          {voices.showVoices && (
            <View style={[a.flex_row, a.align_center, a.gap_xs]}>
              {visibleAvatars.length > 0 && (
                <MiniAvatarStack
                  avatars={visibleAvatars}
                  borderColor={ss.bg.contrast_25}
                />
              )}
              <Text
                style={[a.text_xs, a.font_bold, t.atoms.text_contrast_medium]}>
                {voicesLabel}
              </Text>
            </View>
          )}
        </View>
      </View>
    </Pressable>
  )
}

function MiniAvatarStack({
  avatars,
  borderColor,
}: {
  avatars: {did: string; handle: string; avatarUrl: string}[]
  borderColor: string
}) {
  return (
    <View style={[a.flex_row, a.align_center]}>
      {avatars.map((avatar, i) => (
        <Image
          key={avatar.did}
          accessibilityIgnoresInvertColors
          source={{uri: avatar.avatarUrl}}
          style={{
            width: 20,
            height: 20,
            borderRadius: 999,
            marginLeft: i === 0 ? 0 : -8,
            borderWidth: 1,
            borderColor,
          }}
        />
      ))}
    </View>
  )
}

/* ---------- formatting + labels ---------- */

/**
 * Compact voices label e.g. "5k voices", "123k voices". The compact card uses
 * spaced thousands ("2 847"); the carousel card uses the k/m suffixed form to
 * fit inside 176px alongside the avatar stack.
 */
function formatVoicesLabel(
  n: number,
  _: (m: MessageDescriptor) => string,
): string {
  let compact: string
  if (n >= 1_000_000) {
    compact = `${Math.round(n / 1_000_000)}m`
  } else if (n >= 1_000) {
    compact = `${Math.round(n / 1_000)}k`
  } else {
    compact = String(n)
  }
  return _(msg`${compact} ${plural(n, {one: 'voice', other: 'voices'})}`)
}
